import { AP, PreAP } from '../../constants/types';
import React from 'react';
import { DebugSecureFirewallCheck } from './browser-debug';
import log from 'chameleon/ui-stack/utilities/log';

const { getDebugFirewallCheckDomainsBehavior } = DebugSecureFirewallCheck;
export const getDomains = (userRole, programs) =>
  domains.filter((domain) => {
    const isValidForUserRole = domain.userRoles.has(userRole);
    const isValidForAnyProgram = programs
      .map((program) => domain.programs.has(program))
      .includes(true);
    return isValidForUserRole && isValidForAnyProgram;
  });

const debugDomainBehaviors = getDebugFirewallCheckDomainsBehavior();
export const getDomainsToActions = (userRole, programs) =>
  new Map(
    getDomains(userRole, programs).map((entry) => {
      let debugDomain = debugDomainBehaviors[entry.url];
      const action =
        debugDomain !== undefined ? debugDomain : entry.actionOnBlock;
      if (debugDomain)
        log(`debug: ${entry.url} has rule to ${debugDomain}, if blocked`);
      return [entry.url, action];
    }),
  );

const [DOMAIN_LOADED, DOMAIN_NOT_LOADED] = [true, false];
export const fetchDomains = (domains = []) => {
  const loaded = (domain) => ({ domain, loaded: DOMAIN_LOADED });
  const failed = (domain) => ({ domain, loaded: DOMAIN_NOT_LOADED });
  const domainPromises = domains.map(
    (domain) =>
      // the axios library does not produces expected behavior with no-cors, falling back to fetch
      new Promise((res) =>
        fetch(`//${domain}`, { method: 'HEAD', mode: 'no-cors' })
          .then(() => res(loaded(domain)))
          .catch(() => res(failed(domain))),
      ),
  );
  return Promise.all(domainPromises);
};

export const getBlockedDomains = (userRole, programs) => {
  const domainsToCheck = getDomainsToActions(userRole, [...programs]);
  const domainsToLoadingStatus = fetchDomains(
    Array.from(domainsToCheck.keys()),
  );

  return domainsToLoadingStatus.then((entries) => {
    let warningDomains = [];
    let blockingDomains = [];

    entries.forEach((entry) => {
      if (
        entry.loaded === DOMAIN_NOT_LOADED &&
        domainsToCheck.get(entry.domain) === Actions.BLOCK
      ) {
        blockingDomains = [...blockingDomains, entry.domain];
      } else if (
        entry.loaded === DOMAIN_NOT_LOADED &&
        domainsToCheck.get(entry.domain) === Actions.WARN
      ) {
        warningDomains = [...warningDomains, entry.domain];
      }
    });

    return Promise.resolve({
      [Actions.WARN]: warningDomains,
      [Actions.BLOCK]: blockingDomains,
    });
  });
};

export const Actions = {
  WARN: 'warn',
  BLOCK: 'block',
};

export const UserRoles = {
  STUDENT: 'student',
  TEACHER: 'teacher',
  COORDINATOR: 'coordinator',
  ADMIN: 'admin',
};

const domains = [
  {
    url: 'authorapi.learnosity.com',
    programs: new Set([AP]),
    userRoles: new Set([
      UserRoles.TEACHER,
      UserRoles.COORDINATOR,
      UserRoles.ADMIN,
    ]),
    actionOnBlock: Actions.WARN,
  },
  {
    url: 'reports-va.learnosity.com',
    programs: new Set([AP]),
    userRoles: new Set([
      UserRoles.STUDENT,
      UserRoles.TEACHER,
      UserRoles.COORDINATOR,
      UserRoles.ADMIN,
    ]),
    actionOnBlock: Actions.WARN,
  },
  {
    url: 'items-va.learnosity.com',
    programs: new Set([PreAP, AP]),
    userRoles: new Set([
      UserRoles.STUDENT,
      UserRoles.TEACHER,
      UserRoles.COORDINATOR,
      UserRoles.ADMIN,
    ]),
    actionOnBlock: Actions.WARN,
  },
  {
    url: 'questions-va.learnosity.com',
    programs: new Set([PreAP, AP]),
    userRoles: new Set([
      UserRoles.STUDENT,
      UserRoles.TEACHER,
      UserRoles.COORDINATOR,
      UserRoles.ADMIN,
    ]),
    actionOnBlock: Actions.WARN,
  },
  {
    url: 'assess-va.learnosity.com',
    programs: new Set([PreAP, AP]),
    userRoles: new Set([UserRoles.STUDENT, UserRoles.TEACHER]),
    actionOnBlock: Actions.WARN,
  },
  {
    url: 'events-va.learnosity.com',
    programs: new Set([AP]),
    userRoles: new Set([UserRoles.STUDENT, UserRoles.TEACHER]),
    actionOnBlock: Actions.WARN,
  },
  {
    url: 'sdk.split.io',
    programs: new Set([PreAP, AP]),
    userRoles: new Set([
      UserRoles.STUDENT,
      UserRoles.TEACHER,
      UserRoles.COORDINATOR,
      UserRoles.ADMIN,
    ]),
    actionOnBlock: Actions.BLOCK,
  },
  {
    url: 'api.split.io',
    programs: new Set([PreAP, AP]),
    userRoles: new Set([
      UserRoles.STUDENT,
      UserRoles.TEACHER,
      UserRoles.COORDINATOR,
      UserRoles.ADMIN,
    ]),
    actionOnBlock: Actions.BLOCK,
  },
  {
    url: 'events.split.io',
    programs: new Set([PreAP, AP]),
    userRoles: new Set([
      UserRoles.STUDENT,
      UserRoles.TEACHER,
      UserRoles.COORDINATOR,
      UserRoles.ADMIN,
    ]),
    actionOnBlock: Actions.BLOCK,
  },
  {
    url: 'cdn.split.io',
    programs: new Set([PreAP, AP]),
    userRoles: new Set([
      UserRoles.STUDENT,
      UserRoles.TEACHER,
      UserRoles.COORDINATOR,
      UserRoles.ADMIN,
    ]),
    actionOnBlock: Actions.BLOCK,
  },
  {
    url: 'wistia.com',
    programs: new Set([AP]),
    userRoles: new Set([
      UserRoles.STUDENT,
      UserRoles.TEACHER,
      UserRoles.COORDINATOR,
      UserRoles.ADMIN,
    ]),
    actionOnBlock: Actions.WARN,
  },
  {
    url: 'fast.wistia.net',
    programs: new Set([AP]),
    userRoles: new Set([
      UserRoles.STUDENT,
      UserRoles.TEACHER,
      UserRoles.COORDINATOR,
      UserRoles.ADMIN,
    ]),
    actionOnBlock: Actions.WARN,
  },
  {
    url: 'status.us10.clickhelp.co',
    programs: new Set([AP]),
    userRoles: new Set([
      UserRoles.STUDENT,
      UserRoles.TEACHER,
      UserRoles.COORDINATOR,
      UserRoles.ADMIN,
    ]),
    actionOnBlock: Actions.WARN,
  },
  {
    url: 'cb.academicmerit.com',
    programs: new Set([PreAP, AP]),
    userRoles: new Set([
      UserRoles.TEACHER,
      UserRoles.COORDINATOR,
      UserRoles.ADMIN,
    ]),
    actionOnBlock: Actions.WARN,
  },
];

export const getIconByAction = (action) => {
  if (action === Actions.WARN) {
    return (
      <span className="FirewallCheck__icon FirewallCheck__icon--warning">
        !
      </span>
    );
  } else if (action === Actions.BLOCK) {
    return (
      <span className="FirewallCheck__icon FirewallCheck__icon--blocked">
        X
      </span>
    );
  }
};

export default {
  UserRoles,
  getBlockedDomains,
  getIconByAction,
  Actions,
  __testing__: {
    getDomains,
    getDomainsToActions,
    fetchDomains,
    domains,
  },
};
